import { useEffect, type ReactNode } from "react";

import {
  ApiKeysContext,
  PoliciesContext,
  RolesContext,
} from "@/modules/dashboard/settings/context/AccessDataContext";
import { useAccessApiKeys } from "@/modules/dashboard/settings/hooks/useAccessApiKeys";
import { usePolicies } from "@/modules/dashboard/settings/hooks/usePolicies";
import { useRoles } from "@/modules/dashboard/settings/hooks/useRoles";
import { toastApiError } from "@/shared/utils/toast";

import { ACCESS_KEYS_FETCH_LIMIT } from "../constants/access.constants";

/**
 * The shared catalog for `/app/access/*`: roles, policies and API keys, fetched once for the three views.
 *
 * It publishes the SAME contexts as Settings' `AccessDataProvider`, so every component ported from there —
 * the role editor, the policy editor, the assign-roles drawer — reads its data through the same
 * `useRolesData()` / `usePoliciesData()` / `useApiKeysData()` and needs no adaptation.
 *
 * What it deliberately does NOT provide is `UsersContext`. Settings needs it because its Roles section
 * invalidates the user list after a role delete, next to a user table on the same page. This module has no
 * user surface — people are administered in `/app/users`, which owns its own state — so mounting it would
 * fire a `/internal/users` page plus a `/counts` on every visit for data nothing renders. That is also why
 * this is a second provider rather than a `load` prop on the first: `useAccessUsers` issues those requests
 * from its OWN effects, so the only way to not make them is to not instantiate the hook.
 *
 * Consequence, stated so it is not discovered by a crash: calling `useUsersData()` under this provider
 * throws. Nothing in this module does.
 */
export function AccessCatalogProvider({ children }: { children: ReactNode }) {
  const rolesData = useRoles();
  const policiesData = usePolicies();
  const apiKeysData = useAccessApiKeys();

  // Destructure the (stable, useCallback) loaders so the mount effect depends on them — not on the whole data
  // objects, which change on every fetch and would re-trigger an infinite reload.
  const { load: loadRoles } = rolesData;
  const { load: loadPolicies } = policiesData;
  const { loadWith: loadKeysWith } = apiKeysData;

  // One initial load for the whole module, in parallel. The hooks set `hasNoAccess` (no throw) on a 403, so
  // the toast only fires for genuine errors.
  //
  // Keys are loaded through `loadWith` rather than `load` to raise the page size to the API ceiling; see
  // ACCESS_KEYS_FETCH_LIMIT. Doing it here rather than in the page keeps it to ONE request: the page would
  // otherwise re-issue the listing — and its per-key roles fan-out — on top of the provider's.
  useEffect(() => {
    void loadRoles().catch(toastApiError);
    void loadPolicies().catch(toastApiError);
    void loadKeysWith({ skip: 0, limit: ACCESS_KEYS_FETCH_LIMIT }).catch(toastApiError);
  }, [loadRoles, loadPolicies, loadKeysWith]);

  return (
    <RolesContext.Provider value={rolesData}>
      <PoliciesContext.Provider value={policiesData}>
        <ApiKeysContext.Provider value={apiKeysData}>{children}</ApiKeysContext.Provider>
      </PoliciesContext.Provider>
    </RolesContext.Provider>
  );
}
